import { Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconCheck } from '@tabler/icons-react';
import { AudioTranscriptCard } from 'src/common/components/audio-transcript-card/AudioTranscriptCard';
import { useSegmentPlayback } from 'src/common/components/audio-transcript-card/useSegmentPlayback';
import { useMeetingNoteAudio } from 'src/meeting-note/hooks/useMeetingNoteAudio';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { VerifyCallout } from './VerifyCallout';
import { VerifyOffer } from './VerifyOffer';
import {
  ClientRecordEvidence,
  DocumentEvidence,
  TranscriptEvidence,
  VerifyEvidence,
  VerifyOutcome,
} from './types';

type VerifyResultProps = {
  outcome: VerifyOutcome;
  evidence: VerifyEvidence[];
  /** Meeting note (document) id, used to fetch the recording for playback. */
  meetingNoteId: string | undefined;
  /** For a correction: the agent's plain-language note on how the source differs from the claim. */
  note?: string;
  /** Sidebar is dark (Siesta chats): use light text against the dark background. */
  dark?: boolean;
  /** Hands the verify context off to the normal document editor pass (correction / not-found). */
  onAction: () => void;
};

export function VerifyResult({
  outcome,
  evidence,
  meetingNoteId,
  note,
  dark,
  onAction,
}: VerifyResultProps) {
  const { transcriptItems, documentItems, recordItems, hasSource } = partitionEvidence(evidence);

  const { data: audio } = useMeetingNoteAudio(
    transcriptItems.length > 0 ? meetingNoteId : undefined,
  );
  const playback = useSegmentPlayback();

  // No evidence anywhere — the only genuine "couldn't verify" outcome.
  if (outcome === 'not_found') {
    return <VerifyCallout dark={dark} onAction={onAction} />;
  }

  const differs = outcome === 'correction';
  const distinctClients = [...new Set(recordItems.map(item => item.client))];
  const recordsLabel =
    distinctClients.length === 1 ? `${distinctClients[0]}’s records` : 'client records';
  const textColour = dark ? 'gray.2' : 'gray.8';

  return (
    <Stack gap="sm">
      {audio?.url && transcriptItems.length > 0 && (
        <audio
          ref={playback.audioRef}
          src={audio.url}
          preload="metadata"
          onTimeUpdate={playback.handleTimeUpdate}
          onEnded={playback.handleEnded}
          onError={playback.handleError}
        />
      )}
      <EvidenceHeader
        differs={differs}
        text={headerText(differs, hasSource, recordsLabel)}
        dark={dark}
      />
      {differs && (
        <Text size="textSm" c={dark ? 'gray.3' : 'gray.7'}>
          {note?.trim() || 'Some details differ from the note.'}
        </Text>
      )}
      <TranscriptCards items={transcriptItems} playback={playback} defaultExpanded={differs} />
      <DocumentLines items={documentItems} colour={textColour} />
      {/* Records only when no source evidence — the header names the single client, so a per-line prefix is needed only for several. */}
      {!hasSource && (
        <RecordLines
          items={recordItems}
          colour={textColour}
          withClientPrefix={distinctClients.length > 1}
        />
      )}
      {differs && (
        <VerifyOffer
          label="Would you like me to propose an edit based on this?"
          dark={dark}
          onClick={onAction}
        />
      )}
    </Stack>
  );
}

// "Source" is the material the note was written from (transcript or supporting document); client
// records are separate firm-held data, authoritative only when no source evidence is present.
function partitionEvidence(evidence: VerifyEvidence[]) {
  const transcriptItems = evidence.filter(
    (item): item is TranscriptEvidence => item.kind === 'transcript',
  );
  const documentItems = evidence.filter(
    (item): item is DocumentEvidence => item.kind === 'document',
  );
  const recordItems = evidence.filter(
    (item): item is ClientRecordEvidence => item.kind === 'client_record',
  );
  return {
    transcriptItems,
    documentItems,
    recordItems,
    hasSource: transcriptItems.length > 0 || documentItems.length > 0,
  };
}

function headerText(differs: boolean, hasSource: boolean, recordsLabel: string): string {
  if (differs) return hasSource ? "Doesn't match the source" : "Doesn't match the client records";
  return hasSource ? 'Found in source' : `Found in ${recordsLabel}`;
}

function EvidenceHeader({
  differs,
  text,
  dark,
}: {
  differs: boolean;
  text: string;
  dark?: boolean;
}) {
  return (
    <Group gap="xs" align="center" wrap="nowrap">
      {differs ? (
        <IconAlertTriangle size={14} stroke={1.5} color="var(--mantine-color-dimmed)" aria-hidden />
      ) : (
        <IconCheck size={14} color="var(--mantine-color-success-6)" aria-hidden />
      )}
      <Text size="textSm" fw={FONT_WEIGHTS.semibold} c={dark ? 'white' : 'gray.8'}>
        {text}
      </Text>
    </Group>
  );
}

function TranscriptCards({
  items,
  playback,
  defaultExpanded,
}: {
  items: TranscriptEvidence[];
  playback: ReturnType<typeof useSegmentPlayback>;
  defaultExpanded: boolean;
}) {
  return (
    <>
      {items.map(item => (
        <AudioTranscriptCard
          key={item.segmentId}
          segment={{
            id: item.segmentId,
            speaker: item.speaker,
            text: item.text,
            startSeconds: item.startSeconds,
            endSeconds: item.endSeconds,
          }}
          isPlaying={playback.isPlaying(item.segmentId)}
          progress={playback.progressFor(item.segmentId)}
          onTogglePlay={() =>
            playback.toggle({
              id: item.segmentId,
              startSeconds: item.startSeconds,
              endSeconds: item.endSeconds,
            })
          }
          defaultExpanded={defaultExpanded}
        />
      ))}
    </>
  );
}

function DocumentLines({ items, colour }: { items: DocumentEvidence[]; colour: string }) {
  return (
    <>
      {items.map((item, index) => (
        <Text key={`doc-${index}`} size="textSm" c={colour} style={{ lineHeight: 1.5 }}>
          <Text span fw={FONT_WEIGHTS.semibold}>
            In “{item.file}”:
          </Text>{' '}
          “{item.quote}”
        </Text>
      ))}
    </>
  );
}

function RecordLines({
  items,
  colour,
  withClientPrefix,
}: {
  items: ClientRecordEvidence[];
  colour: string;
  withClientPrefix: boolean;
}) {
  return (
    <>
      {items.map((item, index) => (
        <Text key={`rec-${index}`} size="textSm" c={colour} style={{ lineHeight: 1.5 }}>
          {withClientPrefix && (
            <Text span fw={FONT_WEIGHTS.semibold}>
              {item.client}:{' '}
            </Text>
          )}
          {item.text}
        </Text>
      ))}
    </>
  );
}
